import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import type { Direction } from "@mui/material";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useAtom } from "jotai";
import {
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";

import { useLanguageDirection } from "@/hooks/useLanguageDirection";
import { Slide, ToastContainer } from "react-toastify";
import { themeAtom } from "../store/theme";

import "react-toastify/dist/ReactToastify.css";

export const MUIWrapperContext = createContext<{
	changeDirection: (dir: Direction) => void;
}>({
	changeDirection: () => {},
});

const cacheRtl = createCache({
	key: "muirtl",
	stylisPlugins: [prefixer, rtlPlugin],
});

const emptyCache = createCache({
	key: "meaningless-key",
});

export default function MUIProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const directionFromLanguage = useLanguageDirection();
	const [direction, setDirection] = useState<Direction>(directionFromLanguage);
	const changeDirection = useCallback((dir: Direction) => {
		setDirection(dir);
	}, []);
	useEffect(() => {
		changeDirection(directionFromLanguage);
	}, [directionFromLanguage, changeDirection]);
	const muiWrapperUtils = useMemo(
		() => ({ changeDirection }),
		[changeDirection],
	);

	const [theme] = useAtom(themeAtom);

	// Attach direction to theme object
	const themeWithDirection = useMemo(
		() => ({ ...theme, direction }),
		[theme, direction],
	);

	useEffect(() => {
		const mode = themeWithDirection.palette.mode;
		document.documentElement.dataset.colorScheme = mode;
	}, [themeWithDirection.palette.mode]);

	return (
		<CacheProvider value={direction === "rtl" ? cacheRtl : emptyCache}>
			<MUIWrapperContext.Provider value={muiWrapperUtils}>
				<ThemeProvider theme={themeWithDirection}>
					<CssBaseline />
					<LocalizationProvider dateAdapter={AdapterLuxon}>
						{children}
					</LocalizationProvider>
				</ThemeProvider>
				<ToastContainer
					theme={themeWithDirection.palette.mode}
					transition={Slide}
					position="top-center"
					rtl={direction === "rtl"}
					// The default 320px box squeezes longer messages into a tall
					// ragged column; let them breathe and wrap normally.
					style={{ width: "min(32rem, calc(100vw - 2rem))" }}
					toastStyle={{ alignItems: "flex-start", padding: "12px 14px" }}
					autoClose={6000}
				/>
			</MUIWrapperContext.Provider>
		</CacheProvider>
	);
}
